import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Radio, CreditCard, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { initializePaystackPayment } from '../../../api/subscriptions.api';

export default function UssdSuspendedState({
  subscription,
  onReactivate,
  maintenanceFee: propFee,
  shortCode: propShortCode,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const maintenanceFee =
    propFee ||
    subscription?.product?.price ||
    60000;

  const shortCode =
    propShortCode ||
    subscription?.serviceDetails?.shortCode ||
    '*384*...#';

  const handleConfirmReactivation = async () => {
    setReactivating(true);
    try {
      if (onReactivate) {
        await onReactivate();
        return;
      }

      if (subscription?._id) {
        toast.info('Connecting to Paystack checkout portal...');
        const initRes = await initializePaystackPayment(subscription._id, {
          months: 1,
          callbackUrl: window.location.href,
        });

        if (initRes && initRes.ok && initRes.data?.data?.authorization_url) {
          window.location.href = initRes.data.data.authorization_url;
          return;
        }

        toast.error(initRes?.data?.message || 'Failed to initialize Paystack checkout.');
      } else {
        toast.error('Subscription record not found.');
      }
    } catch (err) {
      toast.error('Failed to initiate Paystack checkout.');
    } finally {
      setReactivating(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 440,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {/* 1. Squircle Icon with warm amber accent */}
      <Box
        sx={{
          width: { xs: 72, sm: 80 },
          height: { xs: 72, sm: 80 },
          borderRadius: '24px',
          bgcolor: '#FEF3C7',
          color: '#D97706',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 24px rgba(217, 119, 6, 0.15)',
          mb: 0.5,
        }}
      >
        <Radio size={38} strokeWidth={1.8} />
      </Box>

      {/* 2. Bold Clean Heading */}
      <Typography
        variant="h1"
        sx={{
          fontWeight: 800,
          fontSize: { xs: '2.2rem', sm: '2.6rem' },
          color: '#111827',
          letterSpacing: '-0.035em',
          lineHeight: 1.15,
          mt: 2.5,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        USSD Offline Channel
      </Typography>

      {/* 3. Concise Subtitle */}
      <Typography
        sx={{
          fontSize: { xs: '1rem', sm: '1.05rem' },
          color: '#6B7280',
          fontWeight: 400,
          mt: 1.5,
          lineHeight: 1.48,
          maxWidth: 420,
        }}
      >
        Channel temporarily paused due to overdue renewal.
      </Typography>

      {/* 4. Focal Status: Big "Paused" with glowing amber indicator */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1.5}
        sx={{ mt: { xs: 3.5, sm: 4.5 } }}
      >
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: '#F59E0B',
            boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.22)',
          }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2.6rem', sm: '3.2rem' },
            color: '#111827',
            letterSpacing: '-0.04em',
            lineHeight: 1.05,
          }}
        >
          Paused
        </Typography>
      </Stack>

      {/* 5. Subtitle / Overdue Fee Note */}
      <Typography
        sx={{
          fontSize: { xs: '0.88rem', sm: '0.95rem' },
          color: '#6B7280',
          fontWeight: 400,
          mt: 1.5,
        }}
      >
        ₦{maintenanceFee.toLocaleString()} monthly maintenance overdue • Grace period ended.
      </Typography>

      {/* 6. Apple Pill Button */}
      <Box sx={{ width: '100%', maxWidth: 440, mt: 4 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => setModalOpen(true)}
          sx={{
            py: 1.6,
            borderRadius: '9999px',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: '#111827',
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#1F2937',
              boxShadow: 'none',
            },
          }}
        >
          Resume USSD Channel
        </Button>
      </Box>

      {/* 7. Footer Reassurance */}
      <Typography
        sx={{
          fontSize: { xs: '0.82rem', sm: '0.88rem' },
          color: '#86868B',
          mt: 1.75,
          maxWidth: 400,
        }}
      >
        Instant reactivation. Customer dialing sessions and short code routing will resume immediately.
      </Typography>

      {/* 8. Feature / Status List with Thin Clean Dividers */}
      <Box sx={{ width: '100%', maxWidth: 440, mt: { xs: 5, sm: 6 }, textAlign: 'left' }}>
        <Box sx={{ borderTop: '1px solid #E5E7EB' }}>
          <Box sx={{ py: 2 }}>
            <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
              Assigned Short Code
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', mt: 0.35 }}>
              {shortCode} (Safely reserved while paused)
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderTop: '1px solid #E5E7EB' }}>
          <Box sx={{ py: 2 }}>
            <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
              Customer Dialing Sessions
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', mt: 0.35 }}>
              Paused until maintenance renewal is settled.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderTop: '1px solid #E5E7EB' }}>
          <Box sx={{ py: 2 }}>
            <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
              Automatic Restoration
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', mt: 0.35 }}>
              Routes instantly to your providers upon payment.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderTop: '1px solid #E5E7EB' }} />
      </Box>

      {/* Reactivation Confirmation Dialog with Paystack Checkout */}
      <Dialog
        open={modalOpen}
        onClose={() => !reactivating && setModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '24px', p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            Resume USSD Channel
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ my: 1 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Monthly Maintenance:
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                ₦{maintenanceFee.toLocaleString()}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Short Code:
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                {shortCode}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} color="#D97706">
                Paused (Awaiting renewal)
              </Typography>
            </Stack>

            <Box sx={{ height: 1, bgcolor: 'divider', my: 0.5 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>
                Total Due to Resume:
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} color="#111827">
                ₦{maintenanceFee.toLocaleString()}
              </Typography>
            </Stack>

            <Typography variant="caption" sx={{ color: '#6B7280', textAlign: 'center' }}>
              Service will be restored instantly upon payment confirmation.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: '14px',
                bgcolor: '#F9FAFB',
                borderColor: '#E5E7EB',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: 'rgba(10, 165, 255, 0.1)',
                      color: '#0AA5FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CreditCard size={18} />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.7rem', fontWeight: 500 }}
                    >
                      Payment Method
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#111827"
                      sx={{ fontSize: '0.82rem' }}
                    >
                      Paystack Secure Checkout
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.68rem' }}
                    >
                      Debit Card, Bank Transfer, USSD
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: '999px',
                    bgcolor: '#ECFDF5',
                    color: '#059669',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <ShieldCheck size={12} />
                  Secured
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setModalOpen(false)}
            disabled={reactivating}
            sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReactivation}
            disabled={reactivating}
            startIcon={
              reactivating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CreditCard size={16} />
              )
            }
            sx={{
              borderRadius: '9999px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#0071E3',
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0077ED', boxShadow: 'none' },
            }}
          >
            {reactivating
              ? 'Opening Paystack...'
              : `Pay with Paystack • ₦${maintenanceFee.toLocaleString()}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

UssdSuspendedState.propTypes = {
  subscription: PropTypes.object,
  onReactivate: PropTypes.func,
  maintenanceFee: PropTypes.number,
  shortCode: PropTypes.string,
};
